import type { ChangeEvent } from "react"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import Image from "@tiptap/extension-image"
import Mention from "@tiptap/extension-mention"
import Placeholder from "@tiptap/extension-placeholder"
import { TextSelection } from "@tiptap/pm/state"
import {
	Editor,
	NodeViewWrapper,
	ReactNodeViewRenderer,
	useEditor,
} from "@tiptap/react"

import {
	attachPastedFiles,
	cn,
	getClipboardFiles,
	getDefaultExtensions,
	Loom,
	ToggleableBlockquote,
	VideoExtension,
} from "@productlane/lib/src/browser"
import { Loader, typographyVariants } from "@productlane/ui"
import CustomInlineCode from "@productlane/ui/src/components/editor/extenstions/custom-inline-code"
import CustomLink from "@productlane/ui/src/components/editor/extenstions/custom-link"
import EmailHistory from "@productlane/ui/src/components/editor/extenstions/email-history"
import { ALLOWED_POSTMARK_ATTACHMENTS_SIZE_IN_MB } from "@productlane/ui/src/constants"

import type { EditorView } from "@tiptap/pm/view"
import { DEFAULT_EDITOR_PROPS } from "@/components/editor/editor-config"
import { EmailSignature } from "@/components/editor/extensions/email-signature"
import { EmojiSuggestion } from "@/components/editor/extensions/emoji"
import { INLINE_IMAGE_REQUEST_EVENT } from "@/components/editor/extensions/slash-commands/commands"
import slashCommands from "@/components/editor/extensions/slash-commands/slash-commands"
import { HelpArticleTriggerExtension } from "@/components/editor/help-articles/help-articles"
import { insertMarkdownPaste } from "@/components/editor/markdown-paste"
import { SnippetTriggerExtension } from "@/components/editor/snippets/snippets"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import suggestions from "@/screens/app/threads/comments/suggestions"
import { useEditorLinks } from "@/screens/app/threads/hooks/useEditorLinks"
import { useEditorState } from "@/screens/app/threads/hooks/useEditorState"
import { isEmptyContentTag } from "../../helpers"
import { useThreadEditor } from "../../hooks/useThreadEditor"
import { isThreadNoteEditable, useThreadContext } from "../../thread-context"
import { BottomBar } from "./bottom-bar"
import { MainContent } from "./main-content"
import { ThreadDeletedBanner } from "./thread-deleted-banner"
import { threadSuggestion } from "./thread-suggestion"
import { TopBar } from "./top-bar"

// Origins that reply via email — inline images are only allowed for these.
// Explicitly listed so that new origins are attachment-only by default until opted in.
const EMAIL_REPLY_ORIGINS = [
	"EMAIL",
	"WIDGET",
	"PORTAL",
	"SUPPORT_PORTAL",
	"API",
	"ZAPIER",
	"HUBSPOT",
	"INTERCOM",
	"INTERCOM_ATTACHMENT",
	"ZENDESK",
	"ZENDESK_ATTACHMENT",
	"FRONT_ATTACHMENT",
	"PLAIN",
	"CALENDAR",
	"AI_CHAT",
	"UPVOTE",
	"INAPP",
] as const

interface CenterContentProps {
	embedded?: boolean
}

export const CenterContent = memo(
	({ embedded = false }: CenterContentProps) => {
		const { role } = useRole()

		const { thread, setIsReply } = useThreadContext()

		const membership = api.user.getMembership.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
		})

		// Emails
		const initialContent = ""

		const {
			content,
			updateContent,
			commentContent,
			updateCommentContent,
			attachmentsHandlers,
			commentAttachmentsHandlers,
		} = useEditorState({
			initialContent,
		})

		const attachmentsHandlersRef = useRef(attachmentsHandlers)
		attachmentsHandlersRef.current = attachmentsHandlers

		const commentAttachmentsHandlersRef = useRef(commentAttachmentsHandlers)
		commentAttachmentsHandlersRef.current = commentAttachmentsHandlers

		// Editor links extraction
		const { extractAndUpdateLinks } = useEditorLinks()

		// Determine if this thread will send as email (inline images only make sense for email).
		// LIVE_CHAT with a finished session also falls through to email.
		const isEmailThread = useMemo(() => {
			if ((EMAIL_REPLY_ORIGINS as readonly string[]).includes(thread.origin)) {
				return true
			}
			// LIVE_CHAT with a finished session sends follow-up via email
			if (thread.origin === "LIVE_CHAT" && thread.liveChatSession?.isFinished) {
				return true
			}
			return false
		}, [thread.origin, thread.liveChatSession?.isFinished])

		// Lazy comment editor — only created on first bottom-bar interaction
		const [commentEditorActivated, setCommentEditorActivated] = useState(false)
		const [commentEditor, setCommentEditor] = useState<Editor | null>(null)
		// handlePaste is built with the editor, so it can only reach it by ref.
		const noteEditorRef = useRef<Editor | null>(null)

		const onCommentUpdateRef = useRef((html: string) => {
			updateCommentContent(html)
		})
		onCommentUpdateRef.current = (html) => {
			updateCommentContent(html)
		}

		const activateCommentEditor = useCallback(() => {
			setCommentEditorActivated(true)
		}, [])

		// Inline image upload for compose editor
		const getFileUrlMutation = api.file.getUrl.useMutation()
		const getFileUrlMutationRef = useRef(getFileUrlMutation)
		getFileUrlMutationRef.current = getFileUrlMutation

		// Legacy SLACK threads (pre-Slack Connect) can't be replied to from here.
		const isLegacySlackThread = thread.origin === "SLACK"

		const composePlaceholder = isLegacySlackThread
			? "Replies aren't available for the legacy Slack integration."
			: "Hit / for formatting, ; for macros, @ for help articles..."

		// The compose editor persists across thread switches, so its Placeholder
		// config is frozen at creation. Read the live value from a ref instead.
		const composePlaceholderRef = useRef(composePlaceholder)
		composePlaceholderRef.current = composePlaceholder

		const uploadInlineImage = useCallback(
			async (file: File, view: EditorView) => {
				const fileSizeMB = file.size / 1024 / 1024
				if (fileSizeMB > ALLOWED_POSTMARK_ATTACHMENTS_SIZE_IN_MB) {
					toast.error({
						title: `Image must be less than ${ALLOWED_POSTMARK_ATTACHMENTS_SIZE_IN_MB}MB.`,
					})
					return
				}

				// Generate a unique upload ID to track this node
				const uploadId = `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`

				// Read file as data URL for instant preview
				const dataUrl = await new Promise<string>((resolve) => {
					const reader = new FileReader()
					reader.onload = () => resolve(reader.result as string)
					reader.readAsDataURL(file)
				})

				// Insert image immediately with local preview and loading state
				const imageNode = view.state.schema.nodes.image?.create({
					src: dataUrl,
					"data-upload-id": uploadId,
					class: "image-uploading",
				})
				if (!imageNode) return

				// Single transaction: insert image + trailing paragraph + cursor.
				const { tr } = view.state
				tr.replaceSelectionWith(imageNode)
				// TipTap's Image extension is block-level by default, so
				// replaceSelectionWith restructures the doc (e.g. cursor at end of
				// "hello" → doc(p("hello"), image)). The arithmetic insertPos +
				// nodeSize then points *before* the image, not after it.
				// Find the image's actual position by its uploadId instead.
				let imageNodePos: number | null = null
				tr.doc.descendants((node, pos) => {
					if (
						node.type.name === "image" &&
						node.attrs["data-upload-id"] === uploadId
					) {
						imageNodePos = pos
						return false
					}
				})
				const paragraphNode = view.state.schema.nodes.paragraph?.create()
				if (paragraphNode) {
					const insertAt: number =
						imageNodePos !== null
							? (imageNodePos as number) + imageNode.nodeSize
							: tr.doc.content.size
					tr.insert(insertAt, paragraphNode)
					tr.setSelection(TextSelection.create(tr.doc, insertAt + 1))
				}
				view.dispatch(tr)

				// Upload to CDN in background
				try {
					const { uploadUrl, fileUrl } =
						await getFileUrlMutationRef.current.mutateAsync({
							fileName: file.name,
							fileType: file.type,
						})

					await fetch(uploadUrl, {
						method: "PUT",
						body: file,
						headers: { "Content-Type": file.type },
					})

					// Find the placeholder node and replace its src
					const { doc } = view.state
					let targetPos: number | null = null
					doc.descendants((node, pos) => {
						if (
							node.type.name === "image" &&
							node.attrs["data-upload-id"] === uploadId
						) {
							targetPos = pos
							return false
						}
					})

					if (targetPos !== null) {
						const updateTr = view.state.tr.setNodeMarkup(targetPos, undefined, {
							...view.state.doc.nodeAt(targetPos)?.attrs,
							src: fileUrl,
							"data-upload-id": null,
							class: null,
						})
						view.dispatch(updateTr)
					}
				} catch (error) {
					console.error("Error uploading inline image:", error)
					toast.error({ title: "Failed to upload image." })

					// Remove the failed placeholder node
					const { doc } = view.state
					let targetPos: number | null = null
					doc.descendants((node, pos) => {
						if (
							node.type.name === "image" &&
							node.attrs["data-upload-id"] === uploadId
						) {
							targetPos = pos
							return false
						}
					})

					if (targetPos !== null) {
						const pos = targetPos as number
						const node = view.state.doc.nodeAt(pos)
						if (node) {
							const deleteTr = view.state.tr.delete(pos, pos + node.nodeSize)
							view.dispatch(deleteTr)
						}
					}
				}
			},
			[],
		)

		// The (editor instance, thread id) pair whose stored draft has been loaded.
		// Tied to the instance so a recreated editor (e.g. on late membership fetch)
		// re-gates empty emits instead of letting them clobber the saved draft.
		const hydratedRef = useRef<{ editor: Editor; threadId: string } | null>(
			null,
		)

		// Compose Editor
		const composeEditor = useEditor(
			{
				shouldRerenderOnTransaction: false,
				extensions: [
					...getDefaultExtensions({ image: false }),
					EmojiSuggestion,
					Placeholder.configure({
						showOnlyWhenEditable: false,
						placeholder: () => composePlaceholderRef.current,
					}),
					SnippetTriggerExtension,
					HelpArticleTriggerExtension,
					EmailHistory,
					EmailSignature,
					ToggleableBlockquote.extend({
						addAttributes() {
							return {
								class: {
									default: null,
									parseHTML: (element) => {
										// Only add class if it's explicitly marked as history
										const isHistory =
											element.hasAttribute("data-history") ||
											element.classList.contains("productlane-quote")
										return isHistory ? "productlane-quote" : null
									},
									renderHTML: (attributes) => {
										return attributes.class
											? {
													class: attributes.class as string,
												}
											: {}
									},
								},
								// Add a data attribute to mark history blockquotes
								"data-history": {
									default: null,
									parseHTML: (element) => element.getAttribute("data-history"),
									renderHTML: (attributes) => {
										return attributes["data-history"]
											? {
													"data-history": attributes["data-history"] as string,
												}
											: {}
									},
								},
							}
						},
					}),
					//? we extending images to preserve the images size inside emails (like signatures, logos)
					Image.extend({
						addAttributes() {
							return {
								...this.parent?.(),
								width: {
									default: null,
									parseHTML: (element) => {
										const width = element.getAttribute("width")
										return width ? parseInt(width, 10) : null
									},
									renderHTML: (attributes) => {
										if (!attributes.width) return {}
										return { width: attributes.width as number }
									},
								},
								height: {
									default: null,
									parseHTML: (element) => {
										const height = element.getAttribute("height")
										return height ? parseInt(height, 10) : null
									},
									renderHTML: (attributes) => {
										if (!attributes.height) return {}
										return { height: attributes.height as number }
									},
								},
								"data-upload-id": {
									default: null,
									parseHTML: (element) =>
										element.getAttribute("data-upload-id"),
									renderHTML: (attributes) => {
										if (!attributes["data-upload-id"]) return {}
										return {
											"data-upload-id": attributes["data-upload-id"] as string,
										}
									},
								},
								class: {
									default: null,
									parseHTML: (element) => element.getAttribute("class"),
									renderHTML: (attributes) => {
										if (!attributes.class) return {}
										return { class: attributes.class as string }
									},
								},
							}
						},
						renderHTML({ HTMLAttributes }) {
							const { width, height, ...rest } = HTMLAttributes
							return [
								"img",
								{
									...rest,
									style:
										width && height
											? `width: ${width as number}px; height: ${height as number}px; max-width: 100%; object-fit: contain;`
											: "max-width: 100%; height: auto;",
								},
							]
						},
						addNodeView() {
							return ReactNodeViewRenderer(
								({
									node,
									selected,
								}: {
									node: { attrs: Record<string, unknown> }
									selected: boolean
								}) => {
									const { src, width, height, class: cls } = node.attrs
									const isUploading = cls === "image-uploading"
									return (
										<NodeViewWrapper
											style={{
												position: "relative",
												display: "inline-block",
												maxWidth: "100%",
												borderRadius: "6px",
												cursor: "default",
											}}
										>
											<img
												alt=""
												src={src as string}
												width={width as number | undefined}
												height={height as number | undefined}
												style={{
													maxWidth: "100%",
													height: "auto",
													display: "block",
													borderRadius: "4px",
													...(isUploading && {
														opacity: 0.45,
														filter: "blur(2px)",
													}),
												}}
											/>
											{selected && !isUploading && (
												<div
													style={{
														position: "absolute",
														inset: 0,
														backgroundColor: "rgba(59, 130, 246, 0.25)",
														borderRadius: "4px",
														pointerEvents: "none",
													}}
												/>
											)}
											{isUploading && (
												<div
													style={{
														position: "absolute",
														inset: 0,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														pointerEvents: "none",
													}}
												>
													<Loader size={9} color="#fff" />
												</div>
											)}
										</NodeViewWrapper>
									)
								},
							)
						},
					}),
					CustomInlineCode,
					CustomLink,
					Loom.configure({
						allowFullscreen: true,
						width: "100%",
						height: "auto",
						HTMLAttributes: { class: "aspect-video rounded-md" },
					}),
					slashCommands.configure({
						suggestion: threadSuggestion,
					}),
				],
				editorProps: {
					...DEFAULT_EDITOR_PROPS,
					attributes: {
						...DEFAULT_EDITOR_PROPS.attributes,
						class: cn(
							typographyVariants({
								variant: "message",
							}),
							"px-2 pb-2 pt-2 focus:outline-none h-full",
						),
					},
					handleDrop: (view, event, _slice, moved) => {
						if (!moved && event.dataTransfer?.files) {
							const files = Array.from(event.dataTransfer.files)
							const imageFiles = files.filter((f) =>
								f.type.startsWith("image/"),
							)
							const nonImageFiles = files.filter(
								(f) => !f.type.startsWith("image/"),
							)

							if (isEmailThread) {
								// Insert images inline for email threads
								for (const file of imageFiles) {
									void uploadInlineImage(file, view)
								}
							} else if (imageFiles.length > 0) {
								// Add images as attachments for non-email threads
								attachmentsHandlersRef.current
									.addAttachments(imageFiles)
									.catch((error) => {
										console.error("Error adding attachments:", error)
									})
							}

							// Add non-image files as attachments
							if (nonImageFiles.length > 0) {
								attachmentsHandlersRef.current
									.addAttachments(nonImageFiles)
									.catch((error) => {
										console.error("Error adding attachments:", error)
									})
							}
							return true
						}
						return false
					},
					handlePaste: (view, event) => {
						const files = getClipboardFiles(event.clipboardData?.items)
						const imageFiles = files.filter((file) =>
							file.type.startsWith("image/"),
						)
						const nonImageFiles = files.filter(
							(file) => !file.type.startsWith("image/"),
						)

						if (imageFiles.length > 0) {
							if (isEmailThread) {
								// Insert images inline for email threads
								for (const file of imageFiles) {
									void uploadInlineImage(file, view)
								}
							} else {
								// Add images as attachments for non-email threads
								void attachmentsHandlersRef.current.addAttachments(imageFiles)
							}
							if (nonImageFiles.length > 0) {
								void attachmentsHandlersRef.current.addAttachments(
									nonImageFiles,
								)
							}
							return true
						}

						return attachPastedFiles(event.clipboardData, (files) =>
							attachmentsHandlersRef.current.addAttachments(files),
						)
					},
				},
				onUpdate({ editor }) {
					if (editor.isDestroyed) return
					const htmlContent = editor.getHTML()
					// A recreated editor emits an empty doc before the stored draft
					// loads; ignore it until the sync effect hydrates this thread.
					const hydrated = hydratedRef.current
					if (
						isEmptyContentTag(htmlContent) &&
						(hydrated?.editor !== editor || hydrated.threadId !== thread.id)
					) {
						return
					}
					updateContent(htmlContent)
					extractAndUpdateLinks(htmlContent)
				},
			},
			[membership.data?.emailSignature],
		)

		// Defer the compose editor's DOM mount one frame after first paint.
		// ProseMirror's first updateStateInner forces a ~240ms layout reflow that
		// otherwise lands inside the click's INP window. Only the first mount
		// pays this cost; the editor persists across thread switches now.
		const [composeEditorVisible, setComposeEditorVisible] = useState(false)
		useEffect(() => {
			const id = requestAnimationFrame(() => setComposeEditorVisible(true))
			return () => cancelAnimationFrame(id)
		}, [])

		// Thread note editor — deferred until the note section is needed
		const hasNoteContent = !isEmptyContentTag(thread.text)
		const [noteEditorRequested, setNoteEditorRequested] = useState(false)
		const noteEditorEnabled = hasNoteContent || noteEditorRequested

		const { editor } = useThreadEditor(
			role !== "VIEWER" && isThreadNoteEditable(thread),
			noteEditorEnabled,
		)

		const isContentEmpty =
			!hasNoteContent && isEmptyContentTag(editor?.getHTML())

		const [isEditorOpen, setEditorOpen] = useState(hasNoteContent)

		// CenterContent no longer remounts on thread switch, so reset per-thread
		// UI state explicitly when the user navigates to a different thread.
		// Note: commentEditorActivated stays true once activated so the lazy
		// comment editor (and its Tiptap BubbleMenu) is reused across threads.
		// Destroying and recreating the editor on every navigation caused
		// React to throw NotFoundError on removeChild because the BubbleMenu
		// plugin detaches its wrapper div from the React tree at registration.
		useEffect(() => {
			setEditorOpen(hasNoteContent)
			setNoteEditorRequested(false)
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [thread.id])

		useEffect(() => {
			if (!composeEditor) return
			// Clear any leftover content from the previous thread before locking,
			// otherwise it stays visible and can't be removed (input is disabled).
			// setEditable also re-runs the Placeholder decoration, so this repaints
			// the origin-specific placeholder on thread switch too.
			if (isLegacySlackThread) {
				composeEditor.setEditable(true)
				composeEditor.commands.clearContent()
				composeEditor.setEditable(false)
			} else {
				composeEditor.setEditable(true)
			}
		}, [composeEditor, isLegacySlackThread])

		useEffect(() => {
			if (!composeEditor || isLegacySlackThread) return
			const next = content ?? initialContent
			if (next === composeEditor.getHTML()) {
				// Same (usually empty) doc means no transaction fires, so the
				// Placeholder decoration keeps the prior thread's origin text.
				// Dispatch an empty transaction to repaint it for this origin.
				composeEditor.view.dispatch(composeEditor.state.tr)
				hydratedRef.current = { editor: composeEditor, threadId: thread.id }
				return
			}
			const scheduledForThreadId = thread.id
			const id = requestAnimationFrame(() => {
				// Abort if the user navigated to a different thread before the
				// frame fired. Without this guard the previous thread's content
				// can land in the new thread's composer.
				if (scheduledForThreadId !== thread.id) return
				composeEditor.commands.setContent(next)
				hydratedRef.current = { editor: composeEditor, threadId: thread.id }
			})
			return () => cancelAnimationFrame(id)
		}, [content, composeEditor, initialContent, thread.id, isLegacySlackThread])

		// Sync the persistent comment editor's content when the user switches
		// threads. Without this, the editor would keep showing the previous
		// thread's draft.
		useEffect(() => {
			if (!commentEditor || commentEditor.isDestroyed) return
			const next = commentContent ?? ""
			if (next === commentEditor.getHTML()) return
			if (commentEditor.isFocused) return
			commentEditor.commands.setContent(next)
		}, [thread.id, commentContent, commentEditor])

		useEffect(() => {
			if (!editor?.isEditable) return
			if (!isContentEmpty) {
				setEditorOpen(true)
			}
		}, [editor?.isEditable, isContentEmpty])

		const onHighlightButtonClick = useCallback(() => {
			if (!isEditorOpen) {
				setNoteEditorRequested(true)
			}
			setEditorOpen(!isEditorOpen)
		}, [isEditorOpen])

		// Comment Editor — created lazily on first bottom-bar interaction
		useEffect(() => {
			if (!commentEditorActivated) return

			const noteExtensions = [
				...getDefaultExtensions({
					image: false,
				}),
				EmojiSuggestion,
				Mention.configure({
					renderLabel: ({ node, options }) => {
						return `${options.suggestion.char}${
							node.attrs.label ?? node.attrs.id
						}`
					},
					HTMLAttributes: {
						class: "font-semibold",
					},
					suggestion: suggestions,
				}),
				Placeholder.configure({
					placeholder: "Hit / for formatting, @ to mention a teammate...",
				}),
				CustomInlineCode,
				VideoExtension,
				slashCommands.configure({
					suggestion: threadSuggestion,
				}),
			]

			const editor = new Editor({
				content: commentContent,
				onUpdate({ editor }) {
					onCommentUpdateRef.current(editor.getHTML())
				},
				extensions: noteExtensions,
				editorProps: {
					...DEFAULT_EDITOR_PROPS,
					attributes: {
						...DEFAULT_EDITOR_PROPS.attributes,
						class: cn(
							typographyVariants({
								variant: "message",
							}),
							"px-2 pb-2 pt-2 focus:outline-none h-full",
						),
					},
					handlePaste: (view, event) => {
						if (
							insertMarkdownPaste({
								view,
								event,
								editor: noteEditorRef.current,
								extensions: noteExtensions,
							})
						) {
							return true
						}

						return attachPastedFiles(event.clipboardData, (files) =>
							commentAttachmentsHandlersRef.current.addAttachments(files),
						)
					},
					handleDrop: (_, event, _slice, moved) => {
						if (!moved && event.dataTransfer?.files) {
							commentAttachmentsHandlersRef.current
								.addAttachments(event.dataTransfer.files)
								.then(() => {
									return true
								})
								.catch((error) => {
									console.error("Error adding attachments:", error)
									return false
								})
							return true
						}
						return false
					},
				},
			})
			noteEditorRef.current = editor
			setCommentEditor(editor)

			return () => {
				editor.destroy()
				noteEditorRef.current = null
				setCommentEditor(null)
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [commentEditorActivated])

		const onMessageUndo = useCallback(
			(content: string) => {
				setIsReply(true)
				composeEditor
					?.chain()
					.setContent(content)
					.focus("end", { scrollIntoView: false })
					.run()
				updateContent(content)
			},
			[setIsReply, composeEditor, updateContent],
		)

		const onHideNotes = useCallback(() => setEditorOpen(false), [])

		const inlineImageInputRef = useRef<HTMLInputElement>(null)

		useEffect(() => {
			const openPicker = () => inlineImageInputRef.current?.click()
			window.addEventListener(INLINE_IMAGE_REQUEST_EVENT, openPicker)
			return () =>
				window.removeEventListener(INLINE_IMAGE_REQUEST_EVENT, openPicker)
		}, [])

		const onInlineImageSelected = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				const file = event.target.files?.[0]
				event.target.value = ""
				if (!file) return
				const view = composeEditor?.view
				if (isEmailThread && view) {
					void uploadInlineImage(file, view)
					return
				}
				void attachmentsHandlersRef.current.addAttachments([file])
			},
			[composeEditor, isEmailThread, uploadInlineImage],
		)

		return (
			<div
				data-thread-conversation
				className={cn(
					"relative flex min-w-0 flex-1 flex-col overflow-hidden border-r border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-900",
					{
						"mx-1 my-2 rounded-md border-0 shadow-md lg:ring-1 lg:ring-black/10 lg:dark:ring-white/15":
							!embedded,
					},
				)}
			>
				<TopBar
					isEditorEmpty={!!editor?.isEmpty}
					isEditorOpen={isEditorOpen}
					onHighlightButtonClick={onHighlightButtonClick}
					embedded={embedded}
				/>
				{thread.isDeleted && <ThreadDeletedBanner />}
				<input
					ref={inlineImageInputRef}
					type="file"
					accept="image/*"
					className="hidden"
					onChange={onInlineImageSelected}
				/>
				<MainContent
					editor={editor}
					onMessageUndo={onMessageUndo}
					isEditorOpen={isEditorOpen}
					onHideNotes={onHideNotes}
				/>
				{!thread.isDeleted && (
					<BottomBar
						// we can't declare these in the BottomBar directly because the ESC handler needs the states of all editors
						commentEditor={commentEditor}
						composeEditor={composeEditor}
						composeEditorVisible={composeEditorVisible}
						onActivateCommentEditor={activateCommentEditor}
					/>
				)}
			</div>
		)
	},
)
CenterContent.displayName = "CenterContent"
